import { useState } from "react";
import {
  Grid,
  Modal,
  Button,
  Form,
  Input,
  Image,
  Row,
  Col,
  Upload,
} from "antd";
import { observer } from "mobx-react";
import { useMobxStores } from "../../stores/stores";
import { EditOutlined } from "@ant-design/icons";
import "../../style.css";
import "./UserModalForm.css";
import LinkedinIcon from "../../assets/social/linkedin-icon.png";
import TwitterIcon from "../../assets/social/x-icon.png";
import GithubIcon from "../../assets/social/github-icon.png";
import MastodonIcon from "../../assets/social/mastodon-icon.png";
import FacebookIcon from "../../assets/social/facebook-icon.png";
import InstagramIcon from "../../assets/social/instagram-icon.png";
import WebIcon from "../../assets/social/web-icon.png";
import {
  isURL,
  isFacebookLink,
  isGitHubLink,
  isInstagramLink,
  isLinkedInLink,
  isMastodonLink,
  isTwitterLink,
} from "../utils/urlValidators";

const { useBreakpoint } = Grid;
const UserModalForm = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm; // < md = mobile
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { userStore } = useMobxStores();

  const [user_form] = Form.useForm();

  const onCreateItem = (fields) => {
    const postData = new FormData();
    postData.append("bio", fields.bio);
    postData.append("first_name", fields.first_name);
    postData.append("last_name", fields.last_name);
    postData.append("email", fields.email);

    if (fields.linkedin) {
      postData.append("linkedin", fields.linkedin);
    }

    if (fields.x) {
      postData.append("x", fields.x);
    }

    if (fields.github) {
      postData.append("github", fields.github);
    }

    if (fields.mastodon) {
      postData.append("mastodon", fields.mastodon);
    }

    if (fields.facebook) {
      postData.append("facebook", fields.facebook);
    }

    if (fields.instagram) {
      postData.append("instagram", fields.instagram);
    }

    if (fields.external_link) {
      postData.append("external_link", fields.external_link);
    }

    if (uploadedImage !== null) {
      postData.append("photo", uploadedImage);
    }

    userStore.UpdateUserProfile(postData);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleOk = () => {
    userStore.setIsUserModalOpen(false);
  };

  const handleCancel = () => {
    userStore.setIsUserModalOpen(false);
  };

  return (
    <div>
      <Modal
        title="Update your profile"
        open={userStore.isUserModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={isMobile ? "90%" : "50%"}
        style={{ textAlign: "center", zIndex: 100000000 }}
        footer={null}
      >
        <Form
          name="user_form"
          initialValues={{
            remember: true,
          }}
          onFinish={onCreateItem}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
          size="large"
          form={user_form}
          fields={userStore.formUserDetails}
        >
          <Form.Item>
            <Image
              src={
                uploadedImage
                  ? URL.createObjectURL(uploadedImage)
                  : userStore.user?.photo
              }
              preview={false}
              className="custom-image"
              style={{ width: "100px", height: "100px" }}
            />
          </Form.Item>
          <Form.Item name="photo">
            <Upload
              maxCount={1}
              showUploadList={false}
              customRequest={() => {}}
              onChange={(file) => {
                setUploadedImage(file.file.originFileObj);
              }}
            >
              <Button icon={<EditOutlined />}>Edit Photo</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            label="First Name"
            name="first_name"
            rules={[
              {
                required: false,
                message: "Please input your First Name!",
              },
            ]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Form.Item
            label="Last Name"
            name="last_name"
            rules={[
              {
                required: false,
                message: "Please input your Last Name!",
              },
            ]}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              {
                required: false,
                message: "Please input your email!",
                type: "email",
              },
            ]}
          >
            <Input disabled={true} placeholder="E-mail" />
          </Form.Item>
          <Form.Item
            label="Bio"
            name="bio"
            rules={[
              {
                required: false,
                message: "Please input your Bio!",
              },
            ]}
          >
            <Input.TextArea placeholder="Bio" />
          </Form.Item>
          <Form.Item>
            <Row align="middle" gutter={16}>
              <Col>
                <img src={LinkedinIcon} alt="linkedin" />
              </Col>
              <Col flex="auto">
                <Form.Item
                  name="linkedin"
                  noStyle
                  rules={[
                    {
                      required: false,
                      type: "url",
                      validator: (_, value) =>
                        !value || isLinkedInLink(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Please enter a valid LinkedIn URL.")
                            ),
                    },
                  ]}
                >
                  <Input
                    placeholder="LinkedIn"
                    defaultValue={userStore.user?.linkedin}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item>
            <Row align="middle" gutter={16}>
              <Col>
                <img src={TwitterIcon} alt="twitter icon" />
              </Col>
              <Col flex="auto">
                <Form.Item
                  name="x"
                  noStyle
                  rules={[
                    {
                      required: false,
                      type: "url",
                      validator: (_, value) =>
                        !value || isTwitterLink(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Please enter a valid X profile URL.")
                            ),
                    },
                  ]}
                >
                  <Input
                    placeholder="X/Twitter"
                    defaultValue={userStore.user?.x}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item>
            <Row align="middle" gutter={16}>
              <Col>
                <img src={GithubIcon} alt="github icon" />
              </Col>
              <Col flex="auto">
                <Form.Item
                  name="github"
                  noStyle
                  rules={[
                    {
                      required: false,
                      type: "url",
                      validator: (_, value) =>
                        !value || isGitHubLink(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("Please enter a valid Github URL")
                            ),
                    },
                  ]}
                >
                  <Input
                    placeholder="Github"
                    defaultValue={userStore.user?.github}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          {isExpanded && (
            <>
              <Form.Item>
                <Row align="middle" gutter={16}>
                  <Col>
                    <img src={MastodonIcon} alt="mastodon icon" />
                  </Col>
                  <Col flex="auto">
                    <Form.Item
                      name="mastodon"
                      noStyle
                      rules={[
                        {
                          required: false,
                          type: "url",
                          validator: (_, value) =>
                            !value || isMastodonLink(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("Please enter a valid Mastodon URL")
                                ),
                        },
                      ]}
                    >
                      <Input
                        placeholder="Mastodon"
                        defaultValue={userStore.user?.mastodon}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item>
                <Row align="middle" gutter={16}>
                  <Col>
                    <img src={FacebookIcon} alt="facebook icon" />
                  </Col>
                  <Col flex="auto">
                    <Form.Item
                      name="facebook"
                      noStyle
                      rules={[
                        {
                          required: false,
                          type: "url",
                          validator: (_, value) =>
                            !value || isFacebookLink(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("Please enter a valid Facebook URL")
                                ),
                        },
                      ]}
                    >
                      <Input
                        placeholder="Facebook"
                        defaultValue={userStore.user?.facebook}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item>
                <Row align="middle" gutter={16}>
                  <Col>
                    <img src={InstagramIcon} alt="instagram icon" />
                  </Col>
                  <Col flex="auto">
                    <Form.Item
                      name="instagram"
                      noStyle
                      rules={[
                        {
                          required: false,
                          type: "url",
                          validator: (_, value) =>
                            !value || isInstagramLink(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(
                                    "Please enter a valid Instagram URL"
                                  )
                                ),
                        },
                      ]}
                    >
                      <Input
                        placeholder="Instagram"
                        defaultValue={userStore.user?.instagram}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item>
                <Row align="middle" gutter={16}>
                  <Col>
                    <img src={WebIcon} alt="web icon" />
                  </Col>
                  <Col flex="auto">
                    <Form.Item
                      name="external_link"
                      noStyle
                      rules={[
                        {
                          required: false,
                          type: "url",
                          validator: (_, value) =>
                            !value || isURL(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("Please enter a valid URL")
                                ),
                        },
                      ]}
                    >
                      <Input
                        placeholder="Website"
                        defaultValue={userStore.user?.external_link}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </>
          )}
          <button
            style={{
              width: "fit-content",
              height: "fit-content",
              display: "block",
              alignSelf: "start",
              color: "#3e83ff",
              cursor: "pointer",
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              padding: 0,
            }}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
          <Form.Item
            wrapperCol={{
              offset: 8,
              span: 24,
            }}
          >
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default observer(UserModalForm);
